import { Flexbox } from '@/ui/flexbox/flexbox';
import { OverflowPillRows } from '@/ui/overflow-pill-rows';
import { EllipsisTextTooltip } from '@/ui/table/infinite-table/ellipsis-text-tooltip';
import { keyframes, styled } from 'goober';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const WORKING_WITH_PILL_GAP = '8px';
const WORKING_WITH_PILL_MAX_WIDTH = '180px';
const WORKING_WITH_PILL_ROW_GAP = '18px';
const WORKING_WITH_SECTION_COLLAPSED_MIN_HEIGHT_PX = 80;
const WORKING_WITH_SECTION_EXPANDED_MIN_HEIGHT_PX = 104;

import type { WorkingWithTeam } from '../stage-pills/spotlight-stage-pill.types';

interface SpotlightCompanyWorkingWithProps {
  onTeamsExpanded?: () => void;
  workingTeams: WorkingWithTeam[];
}

const getVisibleWorkingWithTeams = (
  workingTeams: WorkingWithTeam[],
): WorkingWithTeam[] => workingTeams.filter(team => Boolean(team.name?.trim()));

export const SpotlightCompanyWorkingWith = ({
  onTeamsExpanded,
  workingTeams,
}: SpotlightCompanyWorkingWithProps) => {
  const { t } = useTranslation('spotlight');
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleTeams = useMemo(
    () => getVisibleWorkingWithTeams(workingTeams),
    [workingTeams],
  );
  return (
    <WorkingWithSection
      $isEmpty={visibleTeams.length === 0}
      $isExpanded={isExpanded}
    >
      {visibleTeams.length > 0 && (
        <>
          <WorkingWithTitle>{t('workingWith')}</WorkingWithTitle>
          <OverflowPillRows
            gap={WORKING_WITH_PILL_GAP}
            getItemKey={team => team.id}
            isExpanded={isExpanded}
            items={visibleTeams}
            maxRows={2}
            rowGap={WORKING_WITH_PILL_ROW_GAP}
            onToggleExpand={() => {
              if (!isExpanded) {
                onTeamsExpanded?.();
              }

              setIsExpanded(previous => !previous);
            }}
            renderExpandControl={({ hiddenCount, onToggleExpand }) => (
              <WorkingWithExpandButton
                aria-label={t('expand')}
                type="button"
                onClick={onToggleExpand}
              >
                +{hiddenCount}
              </WorkingWithExpandButton>
            )}
            renderPopoverTrigger={overflowCount => (
              <WorkingWithExpandButton type="button">
                +{overflowCount}
              </WorkingWithExpandButton>
            )}
            renderPill={(team, { index, isRevealedOnExpand }) => {
              const teamName = team.name?.trim() ?? '';

              return (
                <WorkingWithTeamPillTooltip
                  Component={
                    <WorkingWithTeamPill
                      $isRevealedOnExpand={isRevealedOnExpand}
                      $revealIndex={index}
                    />
                  }
                  text={teamName}
                />
              );
            }}
            renderPopoverContent={popoverTeams => (
              <WorkingWithTeamsPopover
                direction="column"
                gap={WORKING_WITH_PILL_GAP}
                rowGap={WORKING_WITH_PILL_ROW_GAP}
              >
                {popoverTeams.map(team => {
                  const teamName = team.name?.trim() ?? '';

                  return (
                    <WorkingWithTeamPillTooltip
                      key={team.id}
                      Component={<WorkingWithTeamPill />}
                      text={teamName}
                    />
                  );
                })}
              </WorkingWithTeamsPopover>
            )}
            showLessLabel={t('showLess')}
          />
        </>
      )}
    </WorkingWithSection>
  );
};

const WorkingWithSection = styled('div')<{
  $isEmpty: boolean;
  $isExpanded: boolean;
}>`
  border-top: 1px solid ${({ theme }) => theme.colors.spotlight.border.default};
  flex-shrink: 0;
  margin-top: auto;
  min-height: ${({ $isEmpty, $isExpanded }) =>
    $isEmpty
      ? 0
      : $isExpanded
        ? WORKING_WITH_SECTION_EXPANDED_MIN_HEIGHT_PX
        : WORKING_WITH_SECTION_COLLAPSED_MIN_HEIGHT_PX}px;
  min-width: 0;
  overflow: hidden;
  padding-top: 14px;
  width: 100%;
  ${({ theme }) =>
    theme.mixins.transition(
      ['min-height'],
      theme.duration.normal,
      theme.easing.out,
    )}

  ${({ $isEmpty }) =>
    $isEmpty
      ? `
    border-top: none;
    padding-top: 0;
  `
      : ''}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const WorkingWithTitle = styled('p')`
  ${({ theme }) => theme.typography.spotlight.sectionLabel}
  color: ${({ theme }) => theme.colors.spotlight.text.muted};
  margin: 0 0 10px 0;
`;

const workingWithPillReveal = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const WorkingWithTeamPill = styled('span')<{
  $isRevealedOnExpand?: boolean;
  $revealIndex?: number;
}>`
  ${({ theme }) => theme.typography.spotlight.teamPill}
  ${({ theme }) => theme.mixins.ellipsis}
  background-color: ${({ theme }) => theme.colors.spotlight.surface.default};
  border: 1px solid ${({ theme }) => theme.colors.spotlight.border.default};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.spotlight.text.primary};
  display: block;
  max-width: min(${WORKING_WITH_PILL_MAX_WIDTH}, 100%);
  min-width: 0;
  padding: 6px 10px;
  width: 100%;

  ${({ theme, $isRevealedOnExpand }) =>
    $isRevealedOnExpand
      ? `
    animation: ${workingWithPillReveal} ${theme.duration.moderate} ${theme.easing.out} forwards;
    opacity: 0;
  `
      : ''}
  animation-delay: ${({ $isRevealedOnExpand, $revealIndex = 0 }) =>
    $isRevealedOnExpand ? `${Math.min($revealIndex, 5) * 28}ms` : '0ms'};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

const WorkingWithTeamPillTooltip = styled(EllipsisTextTooltip)`
  display: block;
  max-width: min(${WORKING_WITH_PILL_MAX_WIDTH}, 100%);
  min-width: 0;
  overflow: hidden;
`;

const WorkingWithExpandButton = styled('button')`
  ${({ theme }) => theme.typography.spotlight.toggleButton}
  background: ${({ theme }) => theme.colors.spotlight.surface.accent};
  border: 1px solid ${({ theme }) => theme.colors.spotlight.surface.accentHover};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.spotlight.primary};
  cursor: pointer;
  flex-shrink: 0;
  padding: 6px 10px;
  white-space: nowrap;
  ${({ theme }) =>
    theme.mixins.transition(
      ['background-color'],
      theme.duration.normal,
      theme.easing.out,
    )}

  &:hover {
    background: ${({ theme }) => theme.colors.spotlight.surface.accentHover};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.spotlight.border.focus};
    outline-offset: 1px;
  }
`;

const WorkingWithTeamsPopover = styled(Flexbox)`
  background-color: ${({ theme }) => theme.colors.basics.white};
  border: 1px solid ${({ theme }) => theme.colors.spotlight.border.default};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow.spotlight.popover};
  margin-bottom: 6px;
  max-width: 240px;
  padding: 12px;

  & > * {
    display: block;
    flex-shrink: 0;
  }
`;
